import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import type {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView
} from 'react-native';

import { isWeb } from '@/shared/utils/platform';

type Mode = 'down' | 'up';

type Options = {
  scrollRef: RefObject<ScrollView | null>;
  hideDelay?: number; // default 2500 ms
  edgeThreshold?: number; // default 20 px
  dyThreshold?: number; // default 5 px
  isActive: boolean;
};

export const useScrollToEdge = ({
  scrollRef,
  hideDelay = 3000,
  edgeThreshold = 20,
  dyThreshold = 5,
  isActive
}: Options) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [mode, setMode] = useState<Mode>('down');
  const lastY = useRef<number>(0);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDragging = useRef<boolean>(false);
  const hasBeenAtBottom = useRef<boolean>(false);

  useEffect(
    () => () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    },
    []
  );

  const resetHideTimer = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setVisible(false), hideDelay);
  };

  const show = (m: Mode) => {
    setMode(m);
    setVisible(true);
    resetHideTimer();
  };

  const hide = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setVisible(false);
  };

  const onScrollBeginDrag = () => {
    isDragging.current = true;
  };

  const onScrollEndDrag = () => {
    isDragging.current = false;
  };

  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
    const y = contentOffset.y;
    const atBottom =
      y + layoutMeasurement.height >= contentSize.height - edgeThreshold;
    const atTop = y <= edgeThreshold;

    if (atBottom) hasBeenAtBottom.current = true;
    if (atTop) hasBeenAtBottom.current = false;

    const dy = y - lastY.current;
    lastY.current = y;

    if (!isWeb && !isDragging.current) return;
    let newMode: Mode | undefined;

    if (dy > dyThreshold && !atBottom && isActive) {
      newMode = 'down';
    }
    if (dy < -dyThreshold && hasBeenAtBottom.current && !atTop && isActive) {
      newMode = 'up';
    }

    if (newMode && newMode != mode) {
      hide();
      resetHideTimer();
      setTimeout(() => show(newMode), 150);
      return;
    } else if (newMode) {
      show(newMode);
    }
  };

  const onPressFab = () => {
    if (mode === 'down') {
      scrollRef.current?.scrollToEnd();
    } else {
      scrollRef.current?.scrollTo({ y: 0 });
    }
    hide(); // disappear immediately on press
  };

  const icon = mode === 'down' ? 'chevron-down' : 'chevron-up';

  return {
    visible,
    mode,
    icon,
    onPressFab,
    onScroll,
    onScrollBeginDrag,
    onScrollEndDrag
  };
};
